import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import { excelMappingApi } from '../api/excelMapping';
import type { ExcelMappingConfig } from '../api/excelMapping';
import { useTranslation } from 'react-i18next';

interface ExcelMappingPreviewProps {
  config: ExcelMappingConfig;
  onBack: () => void;
}

const ExcelMappingPreview: React.FC<ExcelMappingPreviewProps> = ({
  config,
  onBack,
}) => {
  const { t } = useTranslation();
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPreview();
  }, [config]);

  const loadPreview = async () => {
    if (!config.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await excelMappingApi.getPreview(config.id);
      if (response.success && response.data) {
        setPreviewData(response.data);
      } else {
        setError(response.message || t('excelMapping.errors.preview', 'Error al cargar la vista previa'));
      }
    } catch (err) {
      setError(t('excelMapping.errors.preview', 'Error al cargar la vista previa'));
    } finally {
      setLoading(false);
    }
  };

  const getFieldStatus = (field: any, value: any) => {
    if (!value || value === '') {
      return field.isRequired ? 'error' : 'default';
    }
    return 'success';
  };

  const getFieldStatusIcon = (field: any, value: any) => {
    if (!value || value === '') {
      return field.isRequired ? <Error color="error" /> : null;
    }
    return <CheckCircle color="success" />;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('excelMapping.previewTitle', 'Vista Previa')}: {config.name}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadPreview}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            {t('excelMapping.refresh', 'Actualizar')}
          </Button>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={onBack}
          >
            {t('excelMapping.back', 'Volver')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Información de la configuración */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('excelMapping.configInfo', 'Información de la Configuración')}
              </Typography>
              
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('excelMapping.file', 'Archivo')}:</strong> {config.excelFileName}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('excelMapping.status', 'Estado')}:</strong>
                </Typography>
                <Chip
                  label={config.isActive ? t('excelMapping.active', 'Activa') : t('excelMapping.inactive', 'Inactiva')}
                  color={config.isActive ? 'success' : 'default'}
                  size="small"
                />
              </Box>

              {config.description && (
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('common.description', 'Descripción')}:</strong>
                  </Typography>
                  <Typography variant="body2">
                    {config.description}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                {t('excelMapping.configuredFields', 'Campos Configurados')}
              </Typography>

              {config.fields?.map((field, index) => (
                <Box key={index} mb={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {field.targetFieldLabel}
                    </Typography>
                    {field.isRequired && (
                      <Chip label={t('excelMapping.required', 'Requerido')} color="error" size="small" />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {field.excelColumn ? `← ${field.excelColumn}` : t('excelMapping.unmapped', 'Sin mapear')}
                    {field.defaultValue && ` (${t('excelMapping.defShort', 'def')}: ${field.defaultValue})`}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Vista previa de datos */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('excelMapping.mappedDataPreview', 'Vista Previa de Datos Mapeados')}
              </Typography>
              
              {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : previewData.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">
                    {t('excelMapping.noPreviewData', 'No hay datos de vista previa disponibles')}
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {config.fields?.map((field) => (
                          <TableCell key={field.targetField}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {field.targetFieldLabel}
                              {field.isRequired && (
                                <Chip label="*" color="error" size="small" />
                              )}
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.map((row, index) => (
                        <TableRow key={index}>
                          {config.fields?.map((field) => {
                            const value = row[field.targetField] || '';
                            const status = getFieldStatus(field, value);
                            
                            return (
                              <TableCell key={field.targetField}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  {getFieldStatusIcon(field, value)}
                                  <Typography
                                    variant="body2"
                                    color={status === 'error' ? 'error' : 'text.primary'}
                                  >
                                    {value || (field.defaultValue || '')}
                                  </Typography>
                                </Box>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {previewData.length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    {t('excelMapping.showingNExamples', 'Mostrando {{count}} registros de ejemplo', { count: previewData.length })}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Resumen de validación */}
      {previewData.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('excelMapping.validationSummary', 'Resumen de Validación')}
            </Typography>
            
            <Grid container spacing={2}>
              {config.fields?.map((field) => {
                const validCount = previewData.filter(row => 
                  row[field.targetField] || field.defaultValue
                ).length;
                const totalCount = previewData.length;
                const isValid = field.isRequired ? validCount === totalCount : true;
                
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={field.targetField}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {isValid ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Error color="error" />
                      )}
                      <Box>
                        <Typography variant="body2">
                          {field.targetFieldLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {validCount}/{totalCount} {t('excelMapping.validRecords', 'registros válidos')}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ExcelMappingPreview;
